import type { ConfigType } from 'dayjs';
import { useCallback, useState } from 'react';
import { DateRelativePickerModel, TimeUnitModel } from './DateRelativePicker.types';
import { DateTimeFormats, dateToIsoDateFormat, dayjs, Dayjs, useDateTimeFormatter } from '../../utils';

export const removeLeadingZero = (value: string): string => {
  if (value && value.length >= 2) {
    return value.replace(/^0+/, '');
  }

  return value;
};

export const hasClearedValue = (
  newModel: DateRelativePickerModel | undefined,
  oldModel: DateRelativePickerModel | undefined
) => {
  // check if newModel has no value, but value exist in the oldModel
  if (!newModel?.type || newModel?.type !== oldModel?.type) return false;
  if (newModel.type === 'IS_ON') return !newModel.from && oldModel.from;
  if (newModel.type === 'IS_IN_BETWEEN') return !newModel.from && !newModel.to && (oldModel.from || oldModel.to);
  if (newModel.type === 'IS_IN_THE_NEXT') return !newModel.amount && !!oldModel.amount;
  if (newModel.type === 'IS_IN_THE_LAST') return !newModel.amount && !!oldModel.amount;
  if (newModel.type === 'IS_WITHIN_THIS') return !newModel.timeUnit && !!oldModel.timeUnit;
  return false;
};

export const getMinToDate = (
  selectedFrom: string | undefined,
  minToDate: ConfigType | undefined
): Dayjs | undefined => {
  // if nothing selected just return the minToDate
  if (!selectedFrom) return minToDate ? dayjs(minToDate) : undefined;

  // if user selected from date but no minToDate was provided
  if (!minToDate) return selectedFrom ? dayjs(selectedFrom) : undefined;

  return minToDate ? dayjs(minToDate) : undefined;
};

export const getMaxFromDate = (selectedTo: string | undefined, maxFromDate: ConfigType): Dayjs | undefined => {
  // if nothing selected just return the maxFromDate
  if (!selectedTo) return maxFromDate ? dayjs(maxFromDate) : undefined;

  // if user selected from date but no maxFromDate was provided
  if (!maxFromDate) return selectedTo ? dayjs(selectedTo) : undefined;

  return maxFromDate ? dayjs(maxFromDate) : undefined;
};

export const useAmountFieldsHelperText = () => {
  const { dateTimeFormatter } = useDateTimeFormatter();

  const [amountFieldErrorMessage, setAmountFieldErrorMessage] = useState<string>('');

  // Return the helper text for the amount field (IS_IN_THE_NEXT and IS_IN_THE_LAST)
  // Return string that explains the date range for selected days amount
  const getAmountFieldHelperText = useCallback(
    (amount: number | undefined, fieldType: 'IS_IN_THE_LAST' | 'IS_IN_THE_NEXT') => {
      const year = new Date().getFullYear().toString();

      if (amountFieldErrorMessage) return amountFieldErrorMessage;

      if (fieldType === 'IS_IN_THE_NEXT') {
        const dayAfter = new Date();
        dayAfter.setDate(dayAfter.getDate() + (amount ?? 0));

        const now = `${dateTimeFormatter('now', { format: DateTimeFormats.LONG_DAY_MONTH })} ${year}`;
        const after = `${dateTimeFormatter(dayAfter, { format: DateTimeFormats.LONG_DAY_MONTH })} ${dayAfter.getFullYear()}`;

        return amount ? `Today, ${now} - ${after}` : "Date will update relative to today's date";
      }

      const dayBefore = new Date();
      dayBefore.setDate(dayBefore.getDate() - (amount ?? 0));

      const now = `${dateTimeFormatter('now', { format: DateTimeFormats.LONG_DAY_MONTH })} ${year}`;
      const before = `${dateTimeFormatter(dayBefore, { format: DateTimeFormats.LONG_DAY_MONTH })} ${dayBefore.getFullYear()}`;

      return amount ? `${before} - Today, ${now}` : "Date will update relative to today's date";
    },
    [amountFieldErrorMessage, dateTimeFormatter]
  );

  return {
    getAmountFieldHelperText,
    setAmountFieldErrorMessage,
    amountFieldErrorMessage
  };
};

const getStartEndDayOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  const endOfWeek = new Date(now);

  const monday = new Date(startOfWeek.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)));
  const sunday = new Date(endOfWeek.setDate(now.getDate() + ((7 - dayOfWeek) % 7)));

  return {
    monday,
    sunday
  };
};

const getQuarter = () => {
  const currentMonthNumber = new Date().getMonth() + 1;
  const year = new Date().getFullYear().toString();
  if (currentMonthNumber <= 3) return `Jan ${year} - Mar ${year}`;
  if (currentMonthNumber <= 6) return `Apr ${year} - Jun ${year}`;
  if (currentMonthNumber <= 9) return `Jul ${year} - Sep ${year}`;
  return `Oct ${year} - Dec ${year}`;
};

export const useFromToHelperText = () => {
  const { dateTimeFormatter } = useDateTimeFormatter();

  const getFromToHelperText = useCallback(
    (from: string | undefined, to: string | undefined, timeFrom?: string, timeTo?: string) => {
      if (!from && !to) {
        return '';
      }
      const fromTime =
        from && timeFrom
          ? ` ${dateTimeFormatter(mergeDateWithTime(from, timeFrom), { format: DateTimeFormats.TIME })}`
          : ``;
      const toTime =
        to && timeTo ? ` ${dateTimeFormatter(mergeDateWithTime(to, timeTo), { format: DateTimeFormats.TIME })}` : ``;
      if (from && !to) {
        return `Any date on or after ${dateTimeFormatter(from)}${fromTime}`;
      }

      if (to && !from) {
        return `Any date on or before ${dateTimeFormatter(to)}${toTime}`;
      }

      return `From ${dateTimeFormatter(from)}${fromTime} to ${dateTimeFormatter(to)}${toTime}`;
    },
    [dateTimeFormatter]
  );

  return { getFromToHelperText };
};

// Return the helper text for the time unit field
// Return string that explains the date range for selected time unit
export const useTimeUnitFieldHelperText = () => {
  const { dateTimeFormatter } = useDateTimeFormatter();

  const getTimeUnitFieldHelperText = useCallback(
    (timeUnit: TimeUnitModel | undefined) => {
      const year = new Date().getFullYear().toString();

      if (timeUnit === 'DAY') {
        const now = `${dateTimeFormatter('now', { format: DateTimeFormats.LONG_DAY_MONTH })} ${year}`;
        return `Today, ${now}`;
      }
      if (timeUnit === 'WEEK') {
        const { monday: mondayDate, sunday: sundayDate } = getStartEndDayOfWeek();
        const monday = `${dateTimeFormatter(mondayDate, { format: DateTimeFormats.WEEKDAY_LONG_DAY_MONTH })} ${mondayDate.getFullYear()}`;
        const sunday = `${dateTimeFormatter(sundayDate, { format: DateTimeFormats.WEEKDAY_LONG_DAY_MONTH })} ${sundayDate.getFullYear()}`;
        return `${monday} - ${sunday}`;
      }
      if (timeUnit === 'MONTH') return `${dateTimeFormatter('now', { format: DateTimeFormats.LONG_MONTH_YEAR })}`;
      if (timeUnit === 'QUARTER') return getQuarter();
      if (timeUnit === 'YEAR') return year;

      return "Date will update relative to today's date";
    },
    [dateTimeFormatter]
  );
  return {
    getTimeUnitFieldHelperText
  };
};

export const useHelperTextForRelativeDate = () => {
  const { getAmountFieldHelperText } = useAmountFieldsHelperText();
  const { getTimeUnitFieldHelperText } = useTimeUnitFieldHelperText();
  const { getFromToHelperText } = useFromToHelperText();

  return useCallback(
    (model: DateRelativePickerModel) => {
      switch (model.type) {
        case 'IS_IN_THE_LAST':
        case 'IS_IN_THE_NEXT':
          return getAmountFieldHelperText(model.amount, model.type);
        case 'IS_WITHIN_THIS':
          return getTimeUnitFieldHelperText(model.timeUnit);
        case 'IS_IN_BETWEEN':
          return getFromToHelperText(model.from, model.to, model.timeFrom, model.timeTo);
        case 'IS_ON':
          return model.from ? `Is on ${dateToIsoDateFormat(new Date(model.from))}` : '';
        case 'IS_EMPTY':
          return 'Is empty';
      }
      return '';
    },
    [getAmountFieldHelperText, getFromToHelperText, getTimeUnitFieldHelperText]
  );
};

export const mergeDateWithTime = (
  date: string | undefined | null,
  time: string | undefined | null
): string | undefined => {
  if (!date) {
    return undefined;
  }
  return `${date}T${time ?? '00:00'}`;
};
